<template>
  <div class="address-search-container">
    <!-- Label -->
    <label v-if="label" for="address-search-input" class="font-weight-bold">
      {{ label }}
    </label>

    <!-- Input avec autocomplétion -->
    <b-form-group class="mb-0">
      <div class="input-with-icon">
        <b-form-input
          id="address-search-input"
          v-model="searchQuery"
          :maxlength="maxLength"
          :disabled="disabled || !isServiceEnabled"
          :state="searchInputState"
          aria-describedby="address-search-input-live-feedback"
          autocomplete="off"
          class="input-with-icons"
          @keydown="handleKeyDown"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown.down.prevent="navigateDown"
          @keydown.up.prevent="navigateUp"
          @keydown.enter.prevent="selectHighlighted"
          @keydown.esc="closeDropdown"
        />

        <!-- Icône de recherche à gauche -->
        <div class="search-icon-container-left">
          <b-spinner v-if="isSearching" small variant="primary"></b-spinner>
          <b-icon v-else icon="search" class="search-icon"></b-icon>
        </div>
        <!-- Icône de fermeture à droite (visible si du texte est saisi) -->
        <div
          v-show="searchQuery"
          class="clear-icon-container"
          @click="clearInput"
        >
          <svg-icon icon-name="icone-fermer-x" className="clear-icon" />
        </div>
      </div>

      <!-- Liste déroulante des résultats -->
      <div
        v-if="showDropdown && hasResults"
        ref="dropdown"
        class="search-results-dropdown"
        role="listbox"
      >
        <div
          v-for="(result, index) in displayedResults"
          :key="result.Id || index"
          :class="[
            'search-result-item',
            { 'search-result-item-highlighted': index === highlightedIndex },
            { 'search-result-item-container': result.Type === 'Container' },
          ]"
          role="option"
          :aria-selected="index === highlightedIndex"
          tabindex="-1"
          @click="selectResult(result)"
          @mouseenter="highlightedIndex = index"
          @focus="highlightedIndex = index"
        >
          <!-- Conteneur pour l'adresse -->
          <div class="address-content">
            <!-- Texte de l'adresse avec highlight -->
            <span
              class="address-text"
              v-html="highlightMatch(result.Text || result.Description)"
            ></span>

            <!-- Badge pour containers avec nombre d'éléments -->
            <b-badge
              v-if="result.Type === 'Container'"
              variant="default"
              class="container-badge"
            >
              {{ getContainerCountLabel(result) }}
            </b-badge>
          </div>

          <!-- Flèche pour les containers -->
          <b-icon
            v-if="result.Type === 'Container'"
            icon="chevron-right"
            class="ml-auto chevron-icon"
          ></b-icon>
        </div>
      </div>
      <ErreurValidation
        id="address-search-input-live-feedback"
        :message="searchErrorMessage"
        :force-show="!!searchErrorMessage"
      ></ErreurValidation>
    </b-form-group>
  </div>
</template>

<script>
// Helpers
import {
  POSTAL_CODE_REGEX,
  STREET_TYPES_CARDINALS_REGEX,
  APARTMENT_PREFIX_REGEX,
  escapeRegex,
  formatAddressDetailsToAddressFormData,
  isPostalBoxAddressResult,
  isQuebecAddressResult,
} from '@/helpers/AddressHelpers';

// Services
import adresseService from '@/services/api/compte/adresse';
import ErreurValidation from '@/components/formulaire/compte/ErreurValidation';

const IGNORED_SEARCH_CHARACTERS_REGEX = /[,.]/g;
const ALPHANUMERIC_SEARCH_KEY_REGEX = /^[0-9a-zA-Z\u00C0-\u017F]$/;
const INVALID_SEARCH_CHARACTER_REGEX = /[^0-9a-zA-Z\s\u00C0-\u017F,.-]/;
const INVALID_SEARCH_CHARACTERS_REGEX = /[^0-9a-zA-Z\s\u00C0-\u017F,.-]/g;
const INVALID_SEARCH_CHARACTER_MESSAGE_KEY = 'compte.adresse.erreurMessage';
const INVALID_SEARCH_CHARACTER_MESSAGE_FALLBACK = 'MSG02-2-01';
const POSTAL_BOX_MESSAGE_KEY = 'compte.adresse.erreurMessageCasePostale';
const POSTAL_BOX_MESSAGE_FALLBACK = 'MSG02-2-02';
const SERVICE_UNAVAILABLE_MESSAGE_KEY =
  'compte.adresse.serviceRechercheIndisponible';
const SERVICE_UNAVAILABLE_MESSAGE_FALLBACK =
  "Service de recherche d'adresse indisponible";

/**
 * Composant de recherche d'adresse avec autocomplétion
 *
 * Implémente les règles d'affaires:
 * - Cas 1: Recherche par valeur saisie (appel à chaque caractère)
 * - Cas 2: Sélection d'un container (récupération des adresses enfants)
 * - Cas 2: Sélection d'une adresse (récupération des détails)
 *
 */
export default {
  name: 'AdresseRecherche',

  components: {
    ErreurValidation,
  },

  props: {
    label: {
      type: String,
      default: 'Rechercher votre adresse',
    },
    noResultsMessage: {
      type: String,
      default: 'Aucune adresse trouvée',
    },
    containerLabel: {
      type: String,
      default: 'Multiple',
    },
    isServiceEnabled: {
      type: Boolean,
      default: true,
    },
    minQueryLength: {
      type: Number,
      default: 3,
    },
    maxLength: {
      type: Number,
      default: 100,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    language: {
      type: String,
      default: 'fr',
      validator: (value) => ['fr', 'en'].includes(value),
    },
  },

  data() {
    return {
      searchQuery: '',
      searchResults: [],
      showDropdown: false,
      highlightedIndex: -1,
      isSearching: false,
      searchDebounceTimer: null,
      blurTimer: null,
      isFocused: false,
      currentContainerId: null,
      lastKeyPressed: null,
      searchValidationMessage: null,
    };
  },

  computed: {
    /**
     * Filtre les résultats bruts pour ne conserver que ceux dont la province
     * est le Québec (QC). Les résultats mentionnant une autre province
     * canadienne sont exclus.
     */
    quebecResults() {
      return (this.searchResults || []).filter((result) => isQuebecAddressResult(result));
    },

    searchInputState() {
      if (!this.isServiceEnabled) {
        return false;
      }

      return this.searchValidationMessage ? false : null;
    },

    searchErrorMessage() {
      if (!this.isServiceEnabled) {
        return this.getServiceUnavailableMessage();
      }

      return this.searchValidationMessage;
    },

    /**
     * Filtre les résultats pour ne garder que ceux qui contiennent tous les mots recherchés.
     * Les boîtes postales sont conservées : Loqate les retourne normalement, seule leur
     * sélection est bloquée (cf. selectResult) car Address Verify ne les accepte pas.
     *
     * Gestion spéciale pour les codes postaux canadiens:
     * - Détecte le format A1A 1A1 ou A1A1A1
     * - Normalise les espaces pour la comparaison
     */
    filteredResults() {
      if (!this.searchQuery || this.quebecResults.length === 0) {
        return this.quebecResults;
      }

      // Détecter si la recherche contient un code postal
      const containsPostalCode = POSTAL_CODE_REGEX.test(this.searchQuery);

      // Normaliser la requête de recherche
      let normalizedQuery = this.searchQuery
        .trim()
        .toLowerCase()
        .normalize('NFD');
      normalizedQuery = normalizedQuery.replace(
        IGNORED_SEARCH_CHARACTERS_REGEX,
        ' '
      );

      // Retirer les préfixes d'appartement (#, App, Apt) suivis d'un nombre
      normalizedQuery = normalizedQuery.replace(APARTMENT_PREFIX_REGEX, '$2');

      // Si la recherche contient un code postal, normaliser les espaces dans les codes postaux
      if (containsPostalCode) {
        normalizedQuery = normalizedQuery.replace(POSTAL_CODE_REGEX, (match) =>
          match.replace(/\s+/g, '')
        );
      }

      // Exclure les types de rues et points cardinaux du filtre de pertinence
      normalizedQuery = normalizedQuery
        .replace(STREET_TYPES_CARDINALS_REGEX, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      // Diviser la requête normalisée en mots
      const searchWords = normalizedQuery
        .split(/\s+/)
        .filter((word) => word.length > 0);

      // Si aucun mot de recherche, retourner tous les résultats
      if (searchWords.length === 0) {
        return this.quebecResults;
      }

      // Filtrer les résultats
      return (
        this.quebecResults?.filter((result) => {
          // Combiner Text et Description pour la recherche
          let resultText = `${result.Text || ''} ${result.Description || ''}`
            .toLowerCase()
            .normalize('NFD');

          // Si la recherche contient un code postal, normaliser les espaces dans les codes postaux du résultat aussi
          if (containsPostalCode) {
            resultText = resultText.replace(POSTAL_CODE_REGEX, (match) =>
              match.replace(/\s+/g, '')
            );
          }

          // Vérifier que tous les mots de recherche sont présents dans le texte du résultat
          return searchWords.every((word) => resultText.includes(word));
        }) || []
      );
    },

    displayedResults() {
      const filteredResults = this.filteredResults || [];
      if (filteredResults.length > 0) {
        return filteredResults;
      }

      return this.quebecResults || [];
    },

    hasResults() {
      return this.displayedResults && this.displayedResults.length > 0;
    },
  },

  watch: {
    isServiceEnabled(value) {
      if (!value) {
        this.clearSearchDebounceTimer();
        this.clear();
      }
    },
  },

  beforeDestroy() {
    this.clearSearchDebounceTimer();
    this.clearBlurTimer();
  },

  methods: {
    clearSearchDebounceTimer() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    },

    clearBlurTimer() {
      if (this.blurTimer) {
        clearTimeout(this.blurTimer);
        this.blurTimer = null;
      }
    },

    sanitizeSearchQuery(value) {
      return (value || '')
        .replace(INVALID_SEARCH_CHARACTERS_REGEX, '')
        .replace(/\s\s+/g, ' ');
    },

    normalizeSearchQueryForFind(value) {
      return this.sanitizeSearchQuery(value)
        .replace(IGNORED_SEARCH_CHARACTERS_REGEX, ' ')
        .replace(/\s\s+/g, ' ')
        .trim();
    },

    hasInvalidSearchCharacters(value) {
      return INVALID_SEARCH_CHARACTER_REGEX.test(value || '');
    },

    shouldTriggerFindOnInput(key = this.lastKeyPressed) {
      // Sur clavier virtuel Android (Gboard) avec prediction/autocorrection active,
      // le keydown est intercepté par la composition IME et event.key vaut
      // "Unidentified" (ou "Process") au lieu du caractere reellement saisi.
      // On traite ce cas comme une touche inconnue (meme comportement que !key)
      // pour ne pas bloquer la recherche a chaque frappe sur ces claviers.
      if (!key || key === 'Unidentified' || key === 'Process') {
        return true;
      }

      return ALPHANUMERIC_SEARCH_KEY_REGEX.test(key);
    },

    getInvalidSearchCharacterMessage() {
      if (typeof this.$t === 'function') {
        return this.$t(INVALID_SEARCH_CHARACTER_MESSAGE_KEY);
      }

      return INVALID_SEARCH_CHARACTER_MESSAGE_FALLBACK;
    },

    getPostalBoxSearchMessage() {
      if (typeof this.$t === 'function') {
        return this.$t(POSTAL_BOX_MESSAGE_KEY);
      }

      return POSTAL_BOX_MESSAGE_FALLBACK;
    },

    getServiceUnavailableMessage() {
      if (typeof this.$t === 'function') {
        return this.$t(SERVICE_UNAVAILABLE_MESSAGE_KEY);
      }

      return SERVICE_UNAVAILABLE_MESSAGE_FALLBACK;
    },

    resetSearchResults() {
      this.showDropdown = false;
      this.isSearching = false;
      this.searchResults = [];
      this.currentContainerId = null;
    },

    /**
     * Capture la touche pressée au clavier
     */
    handleKeyDown(event) {
      this.lastKeyPressed = event.key;
    },

    /**
     * Cas 1: Gestion de la saisie dans le champ de recherche
     * - Nettoie les espaces avant l'appel AddressFind
     * - Bloque les caractères spéciaux (les cases postales sont autorisées dans la recherche)
     * - Appelle le service de recherche d'adresse avec debounce
     */
    handleInput(value) {
      // Annuler le timer précédent
      this.clearSearchDebounceTimer();

      if (!this.isServiceEnabled) {
        this.lastKeyPressed = null;
        this.searchValidationMessage = null;
        this.resetSearchResults();
        return;
      }

      // Nettoyer la valeur avant l'appel AddressFind. Le tiret (-) est autorisé
      // car il est courant dans les adresses.
      const sanitizedValue = this.sanitizeSearchQuery(value);
      if (sanitizedValue !== value) {
        this.searchQuery = sanitizedValue;
      }
      const searchValue = this.normalizeSearchQueryForFind(value);
      const shouldTriggerFind = this.shouldTriggerFindOnInput();
      this.lastKeyPressed = null;

      if (this.hasInvalidSearchCharacters(value)) {
        this.searchValidationMessage = this.getInvalidSearchCharacterMessage();
        this.resetSearchResults();
        return;
      }

      this.searchValidationMessage = null;

      // Si la query est trop courte, fermer le dropdown
      if (searchValue.length < this.minQueryLength) {
        this.resetSearchResults();
        return;
      }

      if (!shouldTriggerFind) {
        this.isSearching = false;
        return;
      }

      // Indiquer qu'une recherche est en cours
      this.isSearching = true;

      // Debounce de 300ms pour éviter trop d'appels
      this.searchDebounceTimer = setTimeout(async () => {
        await this.performSearch(searchValue);
      }, 300);
    },

    /**
     * Effectue la recherche via le service de recherche d'adresse
     */
    async performSearch(query, containerId = null) {
      try {
        // Appel au service Adresse Find
        this.searchResults = await adresseService.rechercherAdresse(
          query,
          containerId,
          this.language
        );

        // Afficher le dropdown si des résultats
        this.showDropdown = this.searchResults.length > 0;
        this.highlightedIndex = -1;
      } catch (error) {
        console.error('Erreur lors de la recherche:', error);
        this.clear();
        this.$emit('error', error);
      } finally {
        this.isSearching = false;
      }
    },

    handleFocus() {
      this.isFocused = true;
      if (this.searchQuery.length >= this.minQueryLength && this.hasResults) {
        this.showDropdown = true;
      }
    },

    handleBlur() {
      // Délai pour permettre le clic sur un résultat
      this.clearBlurTimer();
      this.blurTimer = setTimeout(() => {
        this.isFocused = false;
        this.showDropdown = false;
        this.blurTimer = null;
      }, 200);
    },

    /**
     * Cas 2: Gestion de la sélection d'un résultat
     */
    async selectResult(result) {
      this.clearBlurTimer();

      // Cas 2.1: Container sélectionné
      if (this.isContainer(result)) {
        const searchText = this.searchQuery || result.Text;
        this.currentContainerId = result.Id;

        // Indiquer qu'une recherche est en cours
        this.isSearching = true;

        // Rechercher les adresses du container
        await this.performSearch(searchText, result.Id);
      }
      // Cas 2.2: Adresse sélectionnée
      else if (this.isAddress(result)) {
        // Afficher le texte de l'adresse dans le champ
        this.searchQuery = result.Text;
        this.showDropdown = false;

        // Les cases postales apparaissent dans les résultats Loqate mais ne peuvent
        // pas être retenues comme adresse du compte : on bloque la sélection ici.
        if (isPostalBoxAddressResult(result)) {
          this.searchValidationMessage = this.getPostalBoxSearchMessage();
          return;
        }

        // Indiquer qu'une récupération est en cours
        this.isSearching = true;

        try {
          // Appel au service Récupération Adresse pour les détails
          const addressDetails = await adresseService.recupererAdresse(
            result.Id
          );

          // Formater les détails pour le store
          const formattedAddress =
            formatAddressDetailsToAddressFormData(addressDetails);

          // Émettre l'événement avec les détails complets
          this.$emit('select', {
            raw: result,
            details: addressDetails,
            formatted: formattedAddress,
          });
        } catch (error) {
          console.error('Erreur lors de la récupération des détails:', error);
          this.clear();
          this.$emit('error', error);
        } finally {
          this.isSearching = false;
        }
      }
    },

    navigateDown() {
      if (!this.showDropdown || !this.hasResults) return;

      if (this.highlightedIndex < this.displayedResults.length - 1) {
        this.highlightedIndex++;
        this.scrollToHighlighted();
      }
    },

    navigateUp() {
      if (!this.showDropdown || !this.hasResults) return;

      if (this.highlightedIndex > 0) {
        this.highlightedIndex--;
        this.scrollToHighlighted();
      }
    },

    selectHighlighted() {
      if (
        this.highlightedIndex >= 0 &&
        this.highlightedIndex < this.displayedResults.length
      ) {
        this.selectResult(this.displayedResults[this.highlightedIndex]);
      }
    },

    closeDropdown() {
      this.showDropdown = false;
      this.highlightedIndex = -1;
    },

    scrollToHighlighted() {
      this.$nextTick(() => {
        if (!this.$refs.dropdown) return;

        const { dropdown } = this.$refs;
        const items = dropdown.querySelectorAll('.search-result-item');

        if (items[this.highlightedIndex]) {
          const item = items[this.highlightedIndex];
          const itemTop = item.offsetTop;
          const itemBottom = itemTop + item.offsetHeight;
          const dropdownScrollTop = dropdown.scrollTop;
          const dropdownHeight = dropdown.clientHeight;

          if (itemBottom > dropdownScrollTop + dropdownHeight) {
            dropdown.scrollTop = itemBottom - dropdownHeight;
          } else if (itemTop < dropdownScrollTop) {
            dropdown.scrollTop = itemTop;
          }
        }
      });
    },

    clear() {
      this.searchQuery = '';
      this.searchResults = [];
      this.showDropdown = false;
      this.highlightedIndex = -1;
      this.isSearching = false;
      this.currentContainerId = null;
      this.searchValidationMessage = null;
    },

    /**
     * Efface le contenu de l'input de recherche
     */
    clearInput() {
      this.searchQuery = '';
      this.searchResults = [];
      this.showDropdown = false;
      this.highlightedIndex = -1;
      this.isSearching = false;
      this.currentContainerId = null;
      this.searchValidationMessage = null;
      // Remettre le focus sur l'input après avoir effacé
      this.$nextTick(() => {
        const input = document.getElementById('address-search-input');
        if (input) {
          input.focus();
        }
      });
    },

    // Helpers pour les containers
    isContainer(result) {
      return adresseService.isContainer(result);
    },

    isAddress(result) {
      return adresseService.isAddress(result);
    },

    getContainerCountLabel(result) {
      return adresseService.getContainerCount(result);
    },

    /**
     * Met en évidence les caractères recherchés dans le texte
     * Chaque mot saisi est mis en évidence individuellement
     */
    highlightMatch(text) {
      if (!text || !this.searchQuery) {
        return text;
      }

      // Diviser la requête en mots (séparer par espaces)
      const words = this.searchQuery
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0);

      // Si aucun mot, retourner le texte tel quel
      if (words.length === 0) {
        return text;
      }

      // Échapper chaque mot et créer un pattern regex avec alternance
      const escapedWords = words.map(escapeRegex);
      const pattern = escapedWords.join('|');

      // Créer une regex insensible à la casse pour trouver toutes les occurrences de n'importe quel mot
      const regex = new RegExp(`(${pattern})`, 'gi');

      // Remplacer les correspondances par une version entourée de <strong>
      return text.replace(regex, '<strong class="highlight-match">$1</strong>');
    },
  },
};
</script>

<style lang="scss" scoped>
.address-search-container {
  position: relative;
  width: 100%;
}

// Dropdown de résultats - styles de lq-web-packages
.search-results-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 365px;
  overflow-y: auto;
  background: white;
  border: 1px solid $couleur-primaire;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: -1px;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: #1912a4 #f7fafc;
  min-width: 100%;
}

// Scrollbar Webkit - styles de lq-web-packages
.search-results-dropdown::-webkit-scrollbar {
  width: 8px;
}

.search-results-dropdown::-webkit-scrollbar-track {
  background: #f7fafc;
}

.search-results-dropdown::-webkit-scrollbar-thumb {
  background-color: #cbd5e0;
  border-radius: 4px;
}

.search-results-dropdown::-webkit-scrollbar-thumb:hover {
  background-color: #a0aec0;
}

// Items de résultat - styles de lq-web-packages
.search-result-item {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease-in-out;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
  width: 100%;
  box-sizing: border-box;
  min-height: 24px;
  white-space: normal;
  background-color: #ffffff;

  &:last-child {
    border-bottom: none;
  }

  &:hover,
  &-highlighted {
    background-color: #f0f9ff;
  }

  &-container {
    font-weight: 500;
    color: $couleur-bleu-fonce-lq;
  }
}

.address-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.address-text {
  word-wrap: break-word;
  word-break: break-word;
  line-height: 1.4;
  color: $couleur-bleu-fonce-lq;

  // Highlight des caractères recherchés
  ::v-deep .highlight-match {
    font-weight: 600;
    color: $couleur-primaire;
    background-color: transparent;
  }
}

.container-badge {
  align-self: flex-start;
}

// Container pour l'input avec icône
.input-with-icon {
  position: relative;
  width: 100%;
}

// Input avec padding à gauche et à droite pour les icônes
.input-with-icons {
  padding-left: 2.5rem;
  padding-right: 2.5rem;
}

// Container de l'icône de recherche / spinner à gauche
.search-icon-container-left {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.75rem;
  pointer-events: none;
  color: #6c757d;
}

// Container de l'icône de fermeture à droite
.clear-icon-container {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.75rem;
  cursor: pointer;
  color: #6c757d;

  &:hover {
    color: #495057;
  }
}

.search-icon {
  font-size: 1.1rem;
}

.clear-icon {
  width: 20px;
  height: 20px;
}

// Icône chevron pour les containers
.chevron-icon {
  color: $couleur-bleu-fonce-lq;
  flex-shrink: 1;
  align-self: center;
}

// Icônes pour les types de résultats
.container-icon {
  color: $couleur-primaire;
  font-size: 1.1rem;
  flex-shrink: 0;
}

.address-icon {
  color: $couleur-primaire;
  font-size: 1.1rem;
  flex-shrink: 0;
}

// Responsive - styles de lq-web-packages
@media (max-width: 640px) {
  .search-results-dropdown {
    max-height: 50vh;
  }
}
</style>
